<template>
  <div id="ServiceHandoverPage">
    <div v-if="isLoading" class="handover-loading">
      <b-spinner></b-spinner>
    </div>
    <div v-else-if="sessionLimitReached" class="handover-main session-limit-main">
      <i class="fa-light fa-circle-exclamation"></i>
      <p class="handover-paragraph handover-paragraph-top">
        You have reached your available session limit. 
      </p>
      <p class="handover-paragraph">
        Please reach out to your employer for more details.
      </p>
      <b-button variant="continue" @click="returnToHome">
        <span>Return to home</span>
      </b-button>
    </div>
    <div v-else-if="serviceData" class="handover-main">
      <div v-if="serviceData.image" class="handover-img-container">
        <img :src="serviceData.image" :alt="serviceData.title" class="handover-img" />
      </div>
      <h2 v-if="serviceData.title" class="handover-title brand-highlight-3">
        {{ serviceData.title }}
      </h2>
      <h3 v-if="serviceData.provider_name" class="handover-subtitle">
        {{ serviceData.provider_name }}
      </h3>
      <p v-if="serviceData.description" class="handover-paragraph">
        {{ serviceData.description }}
      </p>
      <ServiceAccordion :accordionData="accordionData" />
      <b-button :variant="isServiceInternal ? 'continue' : 'external'" @click="handleClick"
        :disabled="isRedirectionLinkLoading" :class="isButtonClickable ? '' : 'btn-unclickable'">
        <span>{{ buttonText }}</span>
        <i v-if="!isServiceInternal && isButtonClickable" class="fa fa-external-link-square" aria-hidden="true"></i>
      </b-button>
    </div>
  </div>
</template>

<script>
import ServiceAccordion from "@/components/Services/ServiceAccordion.vue";
import axios from "axios";
import config from "../config/constants";
import { fetchService } from "@/utils/sessionEligibility/sessionEligibility";
import { returnHome } from "@/utils/embed/partnerEmbed";

export default {
  name: "ServiceHandoverPage",
  components: {
    ServiceAccordion
  },
  data() {
    return {
      serviceData: {},
      SSORedirectionLink: "",
      isRedirectionLinkLoading: false,
      newTab: null,
      sessionLimitReached: false,
      isLoading: true,
    };
  },
  computed: {
    isButtonClickable() {
      return this.serviceData?.web_clickable_cta === 1;
    },
    passedServiceId() {
      return this.$route.params.serviceId;
    },
    isServiceInternal() {
      return (
        this.serviceData.type === "internal"
      );
    },
    buttonText() {
      return this.serviceData.cta_text ? this.serviceData.cta_text : this.isServiceInternal ? "Continue to Booking" : "Continue"
    },
    accordionData() {
      const accordionArray = [];

      if (this.serviceData.how_it_works) {
        accordionArray.push({
          title: "How it works",
          slug: "how_it_works",
          content: this.serviceData.how_it_works
        });
      }

      if (this.serviceData.faqs && this.serviceData.faqs.length) {
        accordionArray.push({
          title: "View FAQs",
          slug: "faqs",
          content: this.serviceData.faqs
        });
      }

      if (
        this.serviceData.related_contents &&
        this.serviceData.related_contents.length > 0
      ) {
        accordionArray.push({
          title: "Related content",
          slug: "related_content",
          content: this.serviceData.related_contents
        });
      }
      return accordionArray;
    }
  },
  mounted() {
    this.$store.dispatch("setServiceData", null);
    this.getService(this.passedServiceId);
  },
  methods: {
    handleClick() {
      if (!this.isButtonClickable) {
        return;
      }

      if (this.isServiceInternal) {
        this.redirectToNextPage()
      } else {
        this.newTab = window.open('about:blank', '_blank');

        this.getRedirectionLink()
      }
    },
    async getService(serviceId) {
      this.isLoading = true;
      try {
        const { service, sessionCapReached, authExpired } = await fetchService(
          this.$store.getters.user_id,
          serviceId
        );
        if (authExpired) {
          this.$store.dispatch("logout");
          return;
        }
        if (sessionCapReached) {
          this.sessionLimitReached = true;
          return;
        }
        if (service) {
          this.serviceData = service;
        }
      } finally {
        this.isLoading = false;
      }
    },
    returnToHome() {
      returnHome(this.$router, { type: "sl:close", v: 1, reason: "service_handover_exit" });
    },
    async redirectToNextPage() {
      let route = "/session-booking"
      if (this.serviceData.cta_to) {
        route = "/" + this.serviceData.cta_to
      }
      await this.$store.dispatch("setServiceData", this.serviceData);
      this.$router.push(route);
    },
    /**
     * Gets the redirection link for the service
     * Ps: At the moment external marketplace services uses OIDC or custom SSO flow.
     * TODO: Add protocol flags so it can be directed correctly to the auth server considering each protocol.
     */
    async getRedirectionLink() {
      if (this.serviceData.sso_identifier) {
        this.isRedirectionLinkLoading = true
        try {
          const slug = this.serviceData.sso_identifier;

          /**
           * If the slug is not connect_plus, starts SSO OIDC flow
           */
          if (slug != 'connect_plus') {
            let data = {
              client_slug: slug
            }
            this.newTab.location.href = process.env.VUE_APP_AUTHSERVER_PATH + '/api/redirect?data=' + btoa(unescape(encodeURIComponent(JSON.stringify(data))));
            return;
          }

          /**
           * Otherwise, it's a custom SSO flow(Connect Plus)
           */
          const payload = {
            user_id: this.$store.getters.user_id,
            slug: slug
          };
          const res = await axios.post(
            config.api_env + "/application/api/p/sso/oauth/redirect-user",
            payload,
            config.options
          );
          if (res.data.success && res.data.data) {
            this.SSORedirectionLink = res.data.data;
            if (this.newTab) {
              this.newTab.location.href = this.SSORedirectionLink;
            }
          } else if (res.data.err && res.data.err.includes("3994")) {
            this.$store.dispatch("logout");
          }
        } catch (err) {
          console.error(err)
        } finally {
          this.isRedirectionLinkLoading = false
        }
      } else if (this.serviceData.cta_to) {
        let self = this;
        if (this.serviceData.cta_to.contains('{{user_token}}')) {
          let newUrl = this.serviceData.cta_to.replace('{{user_token}}', self.$store.getters.user_id);
          window.open(newUrl, "_blank");
        }
        window.open(this.serviceData.cta_to, "_blank");
      }
    }
  }
};
</script>

<style lang="less">
#ServiceHandoverPage {
  min-height: 100%;
  background-color: #fff;
  padding: 40px 20px;
  font-family: DMSans, sans-serif;

  .handover-top {
    padding: 24px 0;
  }

  .handover-loading {
    display: flex;
    justify-content: center;
    padding: 40px 0;
  }

  .handover-main {
    max-width: 540px;
    margin: 0 auto;
    border-radius: 16px;
    padding: 24px 28px;
    text-align: center;
  }

  .session-limit-main {
    max-width: 344px;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .btn {
      width: 100%;
    }
  }

  .fa-circle-exclamation {
    font-size: 8rem;
    color:#FF9600;
    margin-bottom: 24px;
  }

  .handover-title {
    font-family: "Raptor V3 Black";
    font-size: 2.6rem;
    margin: 0 0 8px 0;
    color: #192550;
  }

  .handover-subtitle {
    font-family: DMSans, sans-serif;
    text-align: center;
    color: var(--brand-primary);
    font-size: 1.8rem;
    margin-bottom: 20px;
    font-weight: 700;
    padding: 0;
    letter-spacing: 0;
  }

  .handover-info {
    font-family: DMSans, sans-serif;
    color: #192550;
    font-size: 1.4rem;
    font-weight: 400;
    margin-top: 16px;
  }

  .handover-paragraph {
    font-family: DMSans, sans-serif;
    font-weight: 400;
    font-size: 1.8rem;
    margin-bottom: 20px;
    color: #122130;
    white-space: break-spaces;

    &.handover-paragraph-top {
      font-family: "Raptor V3 Black" !important;
      font-size: 2.5rem;
    }
  }

  .handover-info {
    font-size: 1.4rem;
  }

  .handover-img-container {
    width: 214px;
    height: 214px;
    border-radius: 28px;
    overflow: hidden;
    margin: 0 auto 20px;
  }

  .handover-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  .btn-continue {
    width: fit-content;
    font-family: "DMSans Bold";
    letter-spacing: normal;
    background-color: var(--brand-primary);
    border-radius: 15px;
    color: #ffffff;
    padding: 16px 24px;
    font-weight: 700;
    font-size: 1.6rem;
    margin-bottom: 12px;
  }

  .btn-external {
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 24px;
    background-color: var(--brand-primary);
    border: 0;
    border-radius: 5px;
    color: #FFFFFF;
    font-size: 2rem;
    font-family: "Raptor V3 Black";
    letter-spacing: normal;
    margin: 0 auto;

    .fa {
      color: #FFFFFF;
      font-size: 4rem;
      margin-left: 12px;
    }
  }

  .btn-unclickable {
    &:hover {
      cursor: default;
    }
  }
}
</style>
